import { useCallback, useState } from 'react';
import type { FailedRequest } from '@common/services/api';
import { usePolling } from './use-polling';
import {
  getOrganisationTrialResearch,
  getTrialResearch,
  startOrganisationTrialResearch,
  startTrialResearch,
} from '../actions';
import { ETrialResearchStatus, type TrialResearchStatus } from '../types';

export const RESEARCH_POLL_INTERVAL_MS = 2000;

const FINAL_STATUSES = [
  ETrialResearchStatus.DONE,
  ETrialResearchStatus.PARTIAL,
  ETrialResearchStatus.FAILED,
];

export type ResearchErrorKind = 'not_allowed' | 'rate_limited' | 'invalid' | 'failed';

const classifyError = (error: FailedRequest | undefined, research?: TrialResearchStatus | null): ResearchErrorKind => {
  if (research?.error === 'domain_not_allowed') return 'not_allowed';
  if (error?.status_code === 429 || error?.type === 'too_many_requests') return 'rate_limited';
  if (error?.status_code === 422) return 'invalid';

  return 'failed';
};

export const isResearchFinal = (status?: ETrialResearchStatus | null) => (
  !!status && FINAL_STATUSES.includes(status)
);

type Source =
  | { kind: 'anonymous'; languageLocale: 'nl' | 'en' }
  | { kind: 'organisation'; organisationId: string };

/**
 * Starts research for a domain and polls until it is done, partial or
 * failed. Anonymous research (landing page) is keyed by the returned token;
 * organisation research (onboarding form) by the organisation.
 */
export const useTrialResearch = (source: Source) => {
  const [research, setResearch] = useState<TrialResearchStatus | null>(null);
  const [error, setError] = useState<ResearchErrorKind | null>(null);
  const [isStarting, setIsStarting] = useState(false);

  const token = research?.token ?? null;
  const isPolling = !!research && !isResearchFinal(research.status);

  const start = useCallback(async (domain: string) => {
    setIsStarting(true);
    setError(null);

    try {
      const { data } = source.kind === 'anonymous'
        ? await startTrialResearch(domain, source.languageLocale)
        : await startOrganisationTrialResearch(source.organisationId, domain);

      setResearch(data);

      if (data.status === ETrialResearchStatus.FAILED) {
        setError(classifyError(undefined, data));
      }

      return data;
    } catch (err) {
      setError(classifyError(err as FailedRequest));

      return null;
    } finally {
      setIsStarting(false);
    }
  }, [
    source.kind,
    (source as { languageLocale?: string }).languageLocale,
    (source as { organisationId?: string }).organisationId,
  ]);

  usePolling(async (signal) => {
    try {
      const { data } = source.kind === 'anonymous'
        ? await getTrialResearch(token as string)
        : await getOrganisationTrialResearch(source.organisationId);

      if (signal.cancelled) return;

      setResearch(data);

      if (data.status === ETrialResearchStatus.FAILED) {
        setError(classifyError(undefined, data));
      }
    } catch (err) {
      if (signal.cancelled) return;

      // A vanished token (404) ends the poll; anything else is retried on the next tick.
      if ((err as FailedRequest)?.status_code === 404) {
        setResearch((current) => (current ? { ...current, status: ETrialResearchStatus.FAILED } : current));
        setError('failed');
      }
    }
  }, RESEARCH_POLL_INTERVAL_MS, isPolling && (source.kind === 'organisation' || !!token));

  const reset = useCallback(() => {
    setResearch(null);
    setError(null);
  }, []);

  return {
    research,
    preview: research?.preview ?? null,
    token,
    error,
    isStarting,
    isPolling,
    isDone: isResearchFinal(research?.status) && research?.status !== ETrialResearchStatus.FAILED,
    start,
    reset,
  };
};
